import React, { useCallback, useContext, useRef } from 'react'
import { connect } from 'react-redux'

import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import Subtitle from 'components/Luxkit/Typography/Subtitle'
import HeadData from 'components/Common/HeadData'
import ImageHero from 'components/Common/ImageHero'
import Arrow from 'components/utils/Arrows/Arrow'
import LayoutContainer from 'components/Common/LayoutContainer'

import Pane from 'components/Common/Pane'
import LinePhoneIcon from 'components/Luxkit/Icons/line/LinePhoneIcon'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'

import Image from 'components/Common/Image'
import PaneBody from 'components/Common/Pane/PaneBody'
import Faq from 'components/Common/Faq'
import { scrollToId } from 'lib/window/scrollUtils'
import content from './FlightLandingPageContent'
import Error404Page from 'components/Pages/Error404Page'
import HttpStatus from 'components/Common/HttpStatus'

import { callUsButtonClicked } from 'analytics/eventDefinitions'
import { addGTMEvent } from 'api/googleTagManager'

import config from 'constants/config'
import Clickable from 'components/Common/Clickable'
import SolidPlayIcon from 'components/Luxkit/Icons/solid/SolidPlayIcon'
import TextButton from 'components/Luxkit/Button/TextButton'
import GeoContext from 'contexts/geoContext'
import { HOTEL_PAGE_OFFER_TYPES } from 'constants/offerList'
import OfferList from 'components/OfferList/OfferList'
import { flightsImageId } from 'constants/flight'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'

const TextDescription = styled(Subtitle)`
  margin-top: ${rem(32)};
  position: relative;
`

const ContainerArrow = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  text-align: center;
`

const SameHeightPaneSm = styled(Pane)`
  margin-top: ${rem(32)};

  ${mediaQueryUp.tablet} {
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin-top: 0;
    height: 100%;
  }

  > * + * {
    margin-top: ${rem(12)};
  }
`

const TwoColumnGrid = styled.div`
  display: grid;

  ${mediaQueryUp.tablet} {
    gap: ${rem(12)};
    grid-template-columns: repeat(2, 1fr);
  }
`

const StyledImage = styled(Image)`
  width: 100%;
`

const AirlineLogos = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: ${rem(32)} auto 0;
  max-width: ${rem(550)};
  align-items: center;

  > * {
    margin: ${rem(10)} ${rem(15)} 0;
  }
`

const HeroButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  > * + * {
    margin-top: ${rem(8)};
  }

  ${mediaQueryUp.tablet} {
    flex-direction: row;

    > * + * {
      margin-top: 0;
      margin-left: ${rem(8)};
    }
  }
`

const { header, mainContent, howItWorks } = content

function FlightsLandingPage() {
  const { currentRegionCode, currentContact, currentRegionHasFlights } = useContext(GeoContext)
  const contentByRegion = content[currentRegionCode] || content.AU
  const offerContentRef = useRef<HTMLDivElement>()
  const handleCallUsOnClick = useCallback(() => {
    addGTMEvent(callUsButtonClicked())
  }, [])

  const scrollIntoOfferList = useCallback(() => {
    if (offerContentRef) {
      offerContentRef.current.scrollIntoView()
    }
  }, [offerContentRef])

  if (!currentRegionHasFlights) {
    return <HttpStatus code={404}>
      <Error404Page invalidRegion />
    </HttpStatus>
  }

  const title = `Flight deals from the World’s Best Airlines - ${config.title} ${currentRegionCode}`
  const description = `Call us for flight only deals ${currentContact.local.humanReadable} or book online for luxury hotel + flight packages. Best Price Guarantee on holiday packages.`
  const hasSteps = contentByRegion.steps?.length > 0

  return <>
    <HeadData
      title={title}
      description={description}
    />

    <ImageHero
      alt={header.title}
      aria-hidden="true"
      brightness={-35}
      gravity="auto"
      mobileAspectRatio="8:9"
      tabletAspectRatio="2:1"
      desktopAspectRatio="8:3"
      largeDesktopAspectRatio="3:1"
      id={flightsImageId}
    >
      <LayoutContainer size="sm">
        <Heading
          variant="heading2"
          align="center"
          as="h1"
          colour="neutral-eight"
        >
          {header.title}
        </Heading>
        <TextDescription variant="subtitle1" align="center" colour="neutral-eight">{header.subTitle}</TextDescription>
        <HeroButtonsContainer>
          <TextButton kind="primary" onClick={scrollIntoOfferList}>Browse Hotel + Flight Deals</TextButton>
          <TextButton
            kind="primary"
            startIcon={<LinePhoneIcon />}
            variant="ghost"
            href={`tel:${currentContact.local.number}`}
            onClick={handleCallUsOnClick}
          >
            Call us {currentContact.local.humanReadable}
          </TextButton>
        </HeroButtonsContainer>
      </LayoutContainer>
      <ContainerArrow>
        <Clickable onClick={() => scrollToId('main-content')}>
          <Arrow size={25} direction="down" />
        </Clickable>
      </ContainerArrow>
    </ImageHero>

    <Pane
      axis="vertical"
      id="main-content">
      <PaneBody size="xl-y md">
        <LayoutContainer size="md">
          <Heading
            variant="heading3"
            align="center"
            as="h2"
          >
            {mainContent.title}
          </Heading>

          {contentByRegion.text.map((paragraph, i) => (
            <BodyText variant="large" align="center" as="p" key={i}>{paragraph}</BodyText>
          ))}

          <BodyText variant="large" align="center">
            <AirlineLogos>
              {contentByRegion.airlineLogos.map((airline) => (
                <Image
                  id={airline.id}
                  alt={airline.alt}
                  width={airline.width}
                  key={airline.id}
                />
              ))
              }
            </AirlineLogos>
          </BodyText>
        </LayoutContainer>
      </PaneBody>
    </Pane>

    {hasSteps && (
      <Pane type="light" axis="vertical">
        <PaneBody size="xl-y md">
          <LayoutContainer size="md">
            <Heading variant="heading3" align="center" as="h2">{howItWorks.title}</Heading>
            <BodyText variant="large" align="center" as="p">{howItWorks.subTitle}</BodyText>
            <BodyText variant="large" align="center">
              {howItWorks.video.id && <TextButton kind="secondary" startIcon={<SolidPlayIcon />}>
                Play video
              </TextButton>}
              <br />
            </BodyText>
            <PaneBody size="xl-y">
              {contentByRegion.steps.map((item, i) =>
                <Pane axis="vertical" key={i}>
                  <PaneBody size="lg">
                    <TwoColumnGrid>
                      <SameHeightPaneSm type="clean">
                        <StyledImage
                          width={664}
                          alt={item.title}
                          id={item.image} />
                      </SameHeightPaneSm>
                      <SameHeightPaneSm>
                        <BodyText variant="medium" weight="bold" format="uppercase">
                          Step {i + 1}
                        </BodyText>
                        <BodyText variant="large" weight="bold">{item.title}</BodyText>
                        <BodyText variant="large">{item.text}</BodyText>
                        {item.note && <BodyText variant="large" weight="bold" as="em">
                          {item.note}
                        </BodyText>}
                      </SameHeightPaneSm>
                    </TwoColumnGrid>
                  </PaneBody>
                </Pane>,
              )}
            </PaneBody>
          </LayoutContainer>
        </PaneBody>
      </Pane>
    )}

    <Pane type={hasSteps ? undefined : 'clear'} ref={offerContentRef}>
      <PaneBody size="xl-y">
        <VerticalSpacer gap={32}>
          <Heading align="center" variant="heading3">Browse our hotel and flight packages</Heading>
          <LayoutContainer>
            <OfferList filters={{
              offerTypes: HOTEL_PAGE_OFFER_TYPES(currentRegionCode),
              frontPageVisibleOnly: true,
            }} />
          </LayoutContainer>
        </VerticalSpacer>
      </PaneBody>
    </Pane>

    {contentByRegion.slug && (
      <Pane type={hasSteps ? 'clear' : undefined}>
        <PaneBody size="xl-y">
          <LayoutContainer>
            <VerticalSpacer gap={40}>
              <Heading
                variant="heading3"
                align="center"
                as="h3"
              >
                Frequently Asked Questions
              </Heading>
              <Faq maxToShow={10} slug={contentByRegion.slug}/>
            </VerticalSpacer>
          </LayoutContainer>
        </PaneBody>
      </Pane>
    )}
  </>
}

const mapStateToProps = (state: App.State) => ({
  memberId: state.auth.account.memberId,
})

export default connect(mapStateToProps)(FlightsLandingPage)
